import { useQuery } from "@tanstack/react-query";
import CardReporting, { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import {
  fallbackToZero,
  type PerformanceFilter,
  type QueryFilter,
} from "features/Library/Common/utils/performanceUtils";
import { GenericPerformanceListWithServerPagination } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { peopleReportingService } from "features/Reporting/services/peopleReportingService";
import { type PeopleOutreachItem } from "features/Reporting/types/people";
import { type ColumnOption } from "interfaces/columnOptions";
import { useCallback, useMemo } from "react";
import dateTimeUtils from "utils/dateTimeUtils";
import { fetchPeopleOutreachTablePaginated } from "../queries";
import { type UserAssetTypes } from "features/People/Users/Performance/types";
import { type PaginationParams } from "features/Library/Common/models";

const fetchPeopleOutreachStatistics = async ({ queryKey, signal }: QueryFilter): Promise<CardReportingItem[]> => {
  const result = await peopleReportingService.activity.getPeopleOutreachStatistics(queryKey[1], signal!);

  return [
    {
      stat: fallbackToZero(result.data.DistinctUsers),
      statDescription: "Users",
      popupBody:
        "Total number of users who received some form of outreach, flows, email, etc. during the specified time period.",
    },
    {
      stat: fallbackToZero(result.data.OutreachAttempts),
      statDescription: "Outreach Attempts",
      popupBody:
        "Total number of outreach attempts across all outreach types, flows, email, etc. during the specified time period.",
    },
    {
      stat: fallbackToZero(result.data.AvgOutreach),
      statDescription: "Avg. Outreach/User",
      popupBody:
        "Total number of outreach attempts across all types divided by the total number of participating users during the specified time period.",
    },
  ];
};

export interface Props {
  dateFilter: PerformanceFilter;
  setSelectedDrilldown: (category: UserAssetTypes) => void;
}

export const PeopleOutreach = ({ dateFilter, setSelectedDrilldown }: Props) => {
  const peopleOutreachStatisticsQuery = useQuery({
    queryFn: fetchPeopleOutreachStatistics,
    queryKey: ["people outreach statistics", dateFilter],
  });

  const fetchTableData = useCallback(
    (params: PaginationParams, signal?: AbortSignal) => {
      return fetchPeopleOutreachTablePaginated(dateFilter, params, signal);
    },
    [dateFilter],
  );
  const queryKey = useMemo(() => ["people outreach table", dateFilter] as const, [dateFilter]);

  const columns: ColumnOption<PeopleOutreachItem>[] = useMemo(
    () => [
      {
        name: "Type",
        width: 7,
        sortKey: "OutreachType",
        isSortable: true,
        render(item) {
          return (
            <button className="invisibleButton" onClick={() => setSelectedDrilldown(item.OutreachType)}>
              {item.OutreachType}
            </button>
          );
        },
      },
      {
        name: "Users",
        width: 7,
        sortKey: "UserCount",
        isSortable: true,
        render(item) {
          return <span>{(item.UserCount ?? 0).toLocaleString()}</span>;
        },
      },
      {
        name: "Attempts",
        width: 7,
        sortKey: "OutreachCount",
        isSortable: true,
        render(item) {
          return <span>{(item.OutreachCount ?? 0).toLocaleString()}</span>;
        },
      },
      {
        name: "Last Activity",
        width: 7,
        sortKey: "LastOutreach",
        isSortable: true,
        render(item) {
          return <span>{item.LastOutreach ? dateTimeUtils.formatDate(item.LastOutreach) : "-"}</span>;
        },
      },
    ],
    [setSelectedDrilldown],
  );

  return (
    <div className="tabSpacer">
      <RequestStatusRenderer state={peopleOutreachStatisticsQuery.status}>
        <CardReporting items={peopleOutreachStatisticsQuery.data!} />
      </RequestStatusRenderer>

      <div className="tabSpacer">
        <GenericPerformanceListWithServerPagination
          hideControls
          columns={columns}
          filter={dateFilter}
          fetchPage={fetchTableData}
          queryKeyBase={queryKey}
        />
      </div>
    </div>
  );
};
